<template>
    <TransitionRoot as="template" :show="show">
        <Dialog as="div" class="fixed inset-0 overflow-hidden" @close="close()">
            <div class="absolute inset-0 overflow-hidden">
                <DialogOverlay class="absolute inset-0 bg-gray-500 opacity-75"/>

                <div class="fixed inset-y-0 pl-16 max-w-full right-0 flex">
                    <TransitionChild as="template" enter="transform transition ease-in-out duration-500 sm:duration-700"
                                     enter-from="translate-x-full" enter-to="translate-x-0"
                                     leave="transform transition ease-in-out duration-500 sm:duration-700"
                                     leave-from="translate-x-0" leave-to="translate-x-full">
                        <div class="w-screen">
                            <form class="h-full divide-y divide-gray-200 flex flex-col bg-white shadow-xl"
                                  @submit.prevent="submit">
                                <div class="flex-1 h-0 overflow-y-auto">
                                    <div class="py-6 px-4  bg-gradient-to-b from-pitch-purple to-dark-pitch-purple sm:px-6">
                                        <div class="flex items-center justify-between">
                                            <DialogTitle class="text-lg text-white">
                                                Create Ad-hoc Commission Invoice
                                            </DialogTitle>
                                            <div class="ml-3 h-7 flex items-center">
                                                <button type="button"
                                                        class="bg-dark-pitch-purple rounded-md text-indigo-200 hover:text-white focus:outline-none focus:ring-2 focus:ring-white"
                                                        @click="close()">
                                                    <span class="sr-only">Close panel</span>
                                                    <XIcon class="h-6 w-6" aria-hidden="true"/>
                                                </button>
                                            </div>
                                        </div>
                                        <div class="mt-1">
                                            <p class="text-sm text-indigo-300">
                                                Get started by filling in the information below to create a new
                                                ad-hoc commission invoice.
                                            </p>
                                        </div>
                                    </div>
                                    <SlideOutErrors :show="showErrors === true"/>
                                    <div class="flex-1 flex flex-col justify-between">
                                        <div class="px-4 divide-y divide-gray-200 sm:px-6">
                                            <div class="mt-6 gap-8">

                                                <div class="col-span-1">
                                                    <h3 class="text-lg leading-6 font-medium text-gray-900">Invoice
                                                        Information</h3>
                                                    <div class="mt-4 grid grid-cols-3 gap-4">
                                                        <div>
                                                            <label for="supplier" class="block text-sm text-gray-900 required">
                                                                Supplier
                                                            </label>
                                                            <div class="mt-1">
                                                                <v-select class="h-full mt-1" :options="suppliers" label="name" :reduce="suppliers => suppliers.id" v-model="form.organisation_id" />
                                                            </div>
                                                        </div>
                                                        <div>
                                                            <label for="reference" class="block text-sm text-gray-900 required">
                                                                Reference
                                                            </label>
                                                            <div class="mt-1">
                                                                <input type="text" id="reference"
                                                                       v-model="form.reference"
                                                                       class="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                                       :class="{'border-red-500': form.errors.reference}"
                                                                />
                                                            </div>
                                                        </div>
                                                        <div></div>
                                                    </div>

                                                    <div class="relative overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg block mt-4"
                                                            style="max-height: 750px;overflow-y: scroll;">
                                                            <table
                                                                class="min-w-full table-fixed divide-y divide-gray-300">
                                                                <thead class="bg-gray-50">
                                                                <tr>
                                                                    <th scope="col" class="relative w-12 px-6 sm:w-16 sm:px-8">
                                                                        <input type="checkbox"
                                                                            class="absolute left-4 top-1/2 -mt-2 h-4 w-4 rounded border-gray-300 text-pitch-purple focus:ring-indigo-500 sm:left-6"
                                                                            @change="adhocSelectAll($event)"
                                                                            v-if="invoiceable_commission_lines.length > 0"
                                                                        />
                                                                    </th>
                                                                    <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                                                        Service Name
                                                                    </th>
                                                                    <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                                                        Service Gross Value
                                                                    </th>
                                                                </tr>
                                                                </thead>
                                                                <tbody class="divide-y divide-gray-200 bg-white">
                                                                    <tr v-if="invoiceable_commission_lines.length > 0"
                                                                        v-for="line in invoiceable_commission_lines"
                                                                        :key="line.id"
                                                                        :class="[selectedLines.includes(line.id) && 'bg-gray-50']">
                                                                        <td class="relative w-12 px-6 sm:w-16 sm:px-8">
                                                                            <div v-if="selectedLines.includes(line.id)" class="absolute inset-y-0 left-0 w-0.5 bg-pitch-purple"></div>
                                                                            <input type="checkbox"
                                                                                class="absolute left-4 top-1/2 -mt-2 h-4 w-4 rounded border-gray-300 text-pitch-purple focus:ring-indigo-500 sm:left-6 adhoc-service-lines"
                                                                                :value="line.id"
                                                                                v-model="selectedLines"/>
                                                                        </td>
                                                                        <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                                            {{ line.name }}
                                                                        </td>
                                                                        <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                                            <span v-if="line.commission_costing_type == 'Net'">
                                                                                {{ formatCurrency(line.buy_net_total, line.quote.buy_currency.code, true) }}
                                                                            </span>
                                                                            <span v-else>
                                                                                {{ formatCurrency(line.buy_gross_total, line.quote.buy_currency.code, true) }}
                                                                            </span>
                                                                        </td>
                                                                    </tr>
                                                                    <tr v-else>
                                                                        <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500"></td>
                                                                        <td colspan="3" class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                                            Select a supplier to view available service lines with commission.
                                                                        </td>
                                                                    </tr>
                                                                    <tr v-if="invoiceable_commission_lines.length > 0">
                                                                        <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500"></td>
                                                                        <td class="whitespace-nowrap px-3 py-4 text-sm font-semibold text-gray-500">Total</td>
                                                                        <td class="whitespace-nowrap px-3 py-4 text-sm font-semibold text-gray-500">
                                                                            {{ formatCurrency(invoiceable_commission_lines.reduce((acc, item) => acc + parseFloat(item.buy_gross_total), 0), invoiceable_commission_lines[0].quote.buy_currency.code, true) }}
                                                                        </td>
                                                                    </tr>
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    <div class="mt-4 grid grid-cols-3 gap-4">
                                                        <div>
                                                            <label for="percentage" class="block text-sm text-gray-900 required">
                                                                Percentage
                                                            </label>
                                                            <div class="mt-1">
                                                                <input type="text" id="percentage" v-model="form.percentage"
                                                                       class="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                                       :class="{'border-red-500': form.errors.percentage}"
                                                                />
                                                            </div>
                                                        </div>
                                                        <div>
                                                            <label for="amount" class="block text-sm text-gray-900 required">
                                                                Amount
                                                            </label>
                                                            <div class="mt-1">
                                                                <input type="text" id="amount" v-model="form.amount"
                                                                       disabled="disabled"
                                                                       class="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                                       :class="{'border-red-500': form.errors.amount}"
                                                                />
                                                            </div>
                                                            <div v-if="organisation !== undefined">
                                                                <span class="text-xs text-gray-800 font-normal" v-if="organisation.commission_vat_type == 'net'">VAT will be added on to this amount</span>
                                                                <span class="text-xs text-gray-800 font-normal" v-else>VAT is included in this amount</span>
                                                            </div>
                                                        </div>
                                                        <div></div>
                                                        <div>
                                                            <label for="po_reference" class="block text-sm text-gray-900 required">
                                                                Supplier Invoice Reference
                                                            </label>
                                                            <div class="mt-1">
                                                                <input type="text" id="po_reference" v-model="form.po_reference"
                                                                class="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                                :class="{'border-red-500': form.errors.po_reference}"
                                                                />
                                                            </div>
                                                        </div>
                                                        <div></div>
                                                        <div></div>
                                                        <div>
                                                            <label for="invoiced_at"
                                                                   class="block text-sm text-gray-900 required">
                                                                Invoice Date <span v-if="formattedInvoiceDate">({{ formattedInvoiceDate }})</span>
                                                            </label>
                                                            <div class="mt-1">
                                                                <input type="date" id="invoiced_at"
                                                                       v-model="form.invoiced_at"
                                                                       class="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                                       :class="{'border-red-500': form.errors.invoiced_at}"
                                                                />
                                                            </div>
                                                        </div>
                                                        <div>
                                                            <label for="due_at" class="block text-sm text-gray-900 required">
                                                                Due Date <span v-if="formattedDueDate">({{ formattedDueDate }})</span>
                                                            </label>
                                                            <div class="mt-1">
                                                                <input type="date" id="due_at" v-model="form.due_at"
                                                                       class="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                                       :class="{'border-red-500': form.errors.due_at}"
                                                                />
                                                            </div>
                                                        </div>
                                                        <!-- <div class="col-span-2">
                                                            <label for="ignore_unit4" class="block text-sm text-gray-900">
                                                                Ignore Unit 4 - if this button is ticked then this invoice will not be sent to unit 4. <br />
                                                                These are mainly invoices prior to August 2023. Please check with finance before raising the invoice if you aren't 100% sure.
                                                            </label>
                                                            <div class="mt-1">
                                                                <button type="button" @click="form.ignore_unit4 = !form.ignore_unit4" :class="{'bg-pitch-purple': form.ignore_unit4, 'bg-gray-200': !form.ignore_unit4 }" class="relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none" role="switch" aria-checked="false">
                                                                    <span class="sr-only">Ignore Unit 4</span>
                                                                    <span aria-hidden="true" :class="[form.ignore_unit4 ? 'translate-x-5': 'translate-x-0' ]" class="translate-x-0 pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200"></span>
                                                                </button>
                                                            </div>
                                                        </div> -->
                                                        <div class="col-span-2">
                                                            <label for="payment_instructions"
                                                                   class="block text-sm text-gray-900">
                                                                Payment Instructions
                                                            </label>
                                                            <div class="mt-1">
                                                                <quill-editor content-type="html"
                                                                              v-model:content="form.payment_instructions"
                                                                              :class="{'border-red-500': form.errors.payment_instructions}"
                                                                              class="min-h-[150px] mt-1 block w-full bg-white border border-gray-300 shadow-sm py-2 px-3"
                                                                              theme="snow"></quill-editor>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div class="flex-shrink-0 px-4 py-4 flex justify-end">
                                    <button type="button"
                                            class="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                            @click="close()">
                                        Close
                                    </button>
                                    <button :disabled="form.processing || !form.isDirty" type="button"
                                            class=" ml-4 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm rounded-md text-white bg-green-500 hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:bg-gray-200"
                                            @click="previewInvoice()">
                                        <span>Preview</span>
                                    </button>
                                    <button :disabled="form.processing || !form.isDirty" type="submit"
                                            class=" ml-4 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm rounded-md text-white bg-pitch-purple hover:bg-dark-pitch-purple focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-gray-200">
                                        <span>Save</span>
                                    </button>
                                </div>
                            </form>
                        </div>
                    </TransitionChild>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>
</template>
<script>
import {ref} from 'vue'
import {Dialog, DialogOverlay, DialogTitle, TransitionChild, TransitionRoot} from '@headlessui/vue'
import {XIcon} from '@heroicons/vue/outline'
import {useForm} from '@inertiajs/vue3'
import SlideOutErrors from "@/Components/SlideOutErrors";
import {emitter} from "@/eventHub";
import moment from "moment/moment";

export default {
    components: {
        SlideOutErrors,
        Dialog,
        DialogOverlay,
        DialogTitle,
        TransitionChild,
        TransitionRoot,
        XIcon
    },
    setup(prop, context) {

        const show = ref(false)

        const showErrors = ref(false)

        const suppliers = ref([]);

        const organisation = ref();

        const form = useForm({
            id: null,
            organisation_id: null,
            fixture_id: null,
            reference: null,
            po_reference: null,
            amount: 0,
            due_at: null,
            invoiced_at: null,
            ignore_unit4: false,
            payment_instructions: null,
            quote_service_ids: [],
            percentage: 0
        });

        function close() {
            this.form.reset();
            this.form.clearErrors();
            this.invoiceable_commission_lines = [];
            this.selectedLines = [];
            this.show = false;
        }

        function open() {
            this.getConfig();
            this.form.clearErrors();
            this.show = true;
        }

        const emitter = emitter;

        return {
            show,
            form,
            close,
            open,
            showErrors,
            emitter,
            suppliers,
            organisation
        }
    },
    mounted() {
        emitter.on('openAdhocCommissionSlideout', (fixture) => {
            if (typeof fixture !== 'undefined') {
                this.form.fixture_id = fixture.id
            }
            this.open();
        });
    },
    data() {
        return {
            invoiceable_commission_lines: false,
            selectedLines: []
        }
    },
    computed: {
        formattedInvoiceDate() {
            if (this.form.invoiced_at) {
                return moment(this.form.invoiced_at).format('DD MMM YYYY');
            }
            return '';
        },
        formattedDueDate() {
            if (this.form.due_at) {
                return moment(this.form.due_at).format('DD MMM YYYY');
            }
            return '';
        },
    },
    methods: {
        /**
         * Get and set the new invoice config
         */
        getConfig() {
            axios.get(route('frontend.fixture.commission.configuration', [this.form.fixture_id])).then(result => {

                // loop over each key returned from the config json
                for (var key in result.data) {
                    if (key !== 'suppliers') {
                        // set it against our new invoice form model
                        this.form[key] = result.data[key];
                    } else {
                        this.suppliers = result.data[key];
                    }
                }

            });
        },
        getSupplierServiceLines() {

            this.invoiceable_commission_lines = [];

            axios.get(route('frontend.fixture.commission.get_service_lines', {
                fixture: this.form.fixture_id,
                supplier_id: this.form.organisation_id
            })).then(result => {
                this.invoiceable_commission_lines = result.data.invoiceable_commission_lines;
                this.organisation = result.data.organisation;
            });
        },
        /**
         * Calculate the total amount based on the quote services checked
         */
        calculateAmount() {

            // set base amount
            var amount = 0;

            // fetch the full models of the service lines selected
            var service_lines = Object.values(this.invoiceable_commission_lines).filter(mfsl => Object.values(this.selectedLines).includes(mfsl.id));

            // loop over each service line
            var last_service = null;
            service_lines.forEach(function (ln) {
                // Check commission costing type
                if(ln.commission_costing_type == "Net") {
                    amount += parseFloat(ln.buy_net_total);
                } else {
                    amount += parseFloat(ln.buy_gross_total);
                }

                last_service = ln;
            });

            // Check if we need to apply the VAT calculation
            if(last_service && last_service.supplier.commission_vat_type == 'net') {
                amount = parseFloat(amount + (amount * (1 + (last_service.buy_vat_rate.percentage / 100)) - amount)).toFixed(2);
            }

            if(this.form.percentage === "") {
                this.form.amount = 0.00;
            } else {
                /* update the form
                if (last_service && last_service.supplier.commission_vat_type == 'net') {
                    amount = amount + (amount * (1 + (last_service.buy_vat_rate.percentage / 100)) - amount);
                } */
                this.form.amount = parseFloat(((amount / 100) * parseFloat(this.form.percentage ?? 0))).toFixed(2);
            }
        },
        /**
         * Submit the form
         */
        submit() {
            // add the selected service ids to the form body
            this.form.quote_service_ids = Object.values(this.selectedLines);

            // perform post
            this.form.post(route('frontend.fixture.commission.store', [this.form.fixture_id]), {
                onSuccess: page => {
                    this.show = false;
                    this.showErrors = false;
                    this.form.reset();
                    this.invoiceable_commission_lines = [];
                    this.selectedLines = [];
                },
                onError: errors => {
                    this.showErrors = true;
                }
            });
        },
        /**
         * Preview the invoice and force a download
         */
        previewInvoice() {
            // add the selected service ids to the form body
            this.form.quote_service_ids = Object.values(this.selectedLines);

            axios({
                url: route('frontend.fixture.commission.preview', [this.form.fixture_id]),
                method: "POST",
                data: this.form,
                responseType: 'arraybuffer'
            }).then(r => {
                const path = window.URL.createObjectURL(new Blob([r.data]));
                const link = document.createElement('a');
                link.href = path;
                link.setAttribute('download', 'DRAFT-' + this.form.reference + '.pdf');
                document.body.appendChild(link);
                link.click(); // force download file
            });
        },
        /**
         * Function to select / de-select all service lines
         */
        adhocSelectAll(event) {
            var service_lines = document.getElementsByClassName('adhoc-service-lines');
            this.selectedLines = [];
            if (service_lines.length > 0) {
                for(var s = 0; s < service_lines.length; s++) {
                    var service_line = service_lines[s];
                    if (event.target.checked === true) {
                        service_line.checked = true;
                        this.selectedLines.push(parseInt(service_line.value));
                    } else {
                        service_line.checked = false;
                    }
                }
            }
            this.calculateAmount();

        }
    },
    created() {
        this.moment = moment;
    },
    watch: {
        /**
         * Run calculations when selected lines updated
         */
        selectedLines() {
            this.calculateAmount();
        },
        'form.percentage' () {
            this.calculateAmount();
        },
        'form.organisation_id' () {
            this.getSupplierServiceLines();
        }
    }
}
</script>
